/**
 * 音频总控管理器
 * 处理全局开关和配置
 */
import type { AudioManagerOptions } from './types';

export class AudioManager {
  private musicEnabled: boolean;
  private soundEffectEnabled: boolean;
  private musicVolume: number;
  private soundEffectVolume: number;

  constructor(options: AudioManagerOptions = {}) {
    this.musicEnabled = options.defaultMusicEnabled ?? true;
    this.soundEffectEnabled = options.defaultSoundEffectEnabled ?? true;
    this.musicVolume = options.defaultMusicVolume ?? 1.0;
    this.soundEffectVolume = options.defaultSoundEffectVolume ?? 1.0;
  }

  /**
   * 初始化音频系统
   */
  init(options: AudioManagerOptions = {}): void {
    if (options.defaultMusicEnabled !== undefined) {
      this.musicEnabled = options.defaultMusicEnabled;
    }
    if (options.defaultSoundEffectEnabled !== undefined) {
      this.soundEffectEnabled = options.defaultSoundEffectEnabled;
    }
    if (options.defaultMusicVolume !== undefined) {
      this.musicVolume = options.defaultMusicVolume;
    }
    if (options.defaultSoundEffectVolume !== undefined) {
      this.soundEffectVolume = options.defaultSoundEffectVolume;
    }
  }

  /**
   * 设置音乐全局开关
   */
  setMusicEnabled(isEnabled: boolean): void {
    this.musicEnabled = isEnabled;
  }

  /**
   * 获取音乐全局开关状态
   */
  getMusicEnabled(): boolean {
    return this.musicEnabled;
  }

  /**
   * 设置音效全局开关
   */
  setSoundEffectEnabled(isEnabled: boolean): void {
    this.soundEffectEnabled = isEnabled;
  }

  /**
   * 获取音效全局开关状态
   */
  getSoundEffectEnabled(): boolean {
    return this.soundEffectEnabled;
  }

  /**
   * 设置音乐总音量
   */
  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));
  }

  /**
   * 获取音乐总音量
   */
  getMusicVolume(): number {
    return this.musicVolume;
  }

  /**
   * 设置音效总音量
   */
  setSoundEffectVolume(volume: number): void {
    this.soundEffectVolume = Math.max(0, Math.min(1, volume));
  }

  /**
   * 获取音效总音量
   */
  getSoundEffectVolume(): number {
    return this.soundEffectVolume;
  }

  /**
   * 计算实际播放音量
   * 实际播放音量 = 资源音量 × 单次播放音量 × 全局音量
   */
  calculateMusicVolume(resourceVolume: number = 1.0, playVolume: number = 1.0): number {
    if (!this.musicEnabled) return 0;
    return Math.max(0, Math.min(1, resourceVolume * playVolume * this.musicVolume));
  }

  /**
   * 计算实际播放音量
   * 实际播放音量 = 资源音量 × 单次播放音量 × 全局音量
   */
  calculateSoundEffectVolume(resourceVolume: number = 1.0, playVolume: number = 1.0): number {
    if (!this.soundEffectEnabled) return 0;
    return Math.max(0, Math.min(1, resourceVolume * playVolume * this.soundEffectVolume));
  }
}

